import * as t from 'io-ts';
import {values} from 'lodash';

import {makeEnumRuntimeType, RLazyTeammateIdRef} from './apiTypes';
import {parseRecipient, RRecipient} from './recipientModel';

/*
 * Runtime type.
 */

export enum ConversationTrackerStagesEnum {
  /**
   * The teammate was once a conversation participant but their tracker has since been revoked.
   * The teammate no longer has conversation access unless they currently have access to one of the conversation's inboxes.
   */
  NONE = 'none',
  /** The teammate unsubscribed/muted the conversation, but still has conversation access. */
  TRACKER = 'tracker',
  /** The teammate is subscribed to the conversation and has conversation access. */
  SUBSCRIBER = 'follower',
  /** The teammate is the current assignee of the conversation and has conversation access. */
  ASSIGNEE = 'assignee',
}

export enum ConversationTrackerStatusesEnum {
  INBOX = 'inbox',
  ARCHIVED = 'archived',
  TRASHED = 'trashed',
  SPAM = 'spam',
}

export enum ConversationTrackerFlagsEnum {
  UNSNOOZED = 'unsnoozed',
  NO_AUTO_FOLLOW = 'no_auto_follow',
  SKIP_INBOX = 'skip_inbox',
  SHARE_FUTURE = 'share_future',
  OWNER = 'owner',
}

export const RConversationTrackerStatus = makeEnumRuntimeType(ConversationTrackerStatusesEnum);
export const RConversationTrackerStage = makeEnumRuntimeType(ConversationTrackerStagesEnum);

const RConversationTrackerDirty = t.readonly(
  t.intersection([
    t.type({
      teammateId: RLazyTeammateIdRef,
      stage: RConversationTrackerStage,
      status: RConversationTrackerStatus,
      extra: t.readonlyArray(makeEnumRuntimeType(ConversationTrackerFlagsEnum)),
      bumpedAt: t.Integer,
      seenAt: t.union([t.undefined, t.Integer]),
    }),
    t.partial({
      /** Batch ID for offline tracker bump actions. */
      offlineBumpedId: t.union([t.undefined, t.string]),
      autoSeenAt: t.union([t.undefined, t.Integer]),
      recipient: t.union([t.undefined, RRecipient]),
    }),
  ]),
);

/*
 * Type.
 */

export interface ConversationTrackerModel extends t.TypeOf<typeof RConversationTrackerDirty> {}
export const RConversationTracker = t.clean<ConversationTrackerModel>(RConversationTrackerDirty as any);

/*
 * IO methods.
 */

export function parseConversationTracker(srcTracker: any, srcSeenBy: any = []) {
  const seenBy = srcSeenBy.find((s: any) => s.teammate_id === srcTracker.teammate_id);
  const seenAt = seenBy && seenBy.seen_at;
  const autoSeenAt = seenBy && seenBy.auto_seen_at;

  return {
    teammateId: srcTracker.teammate_id,
    stage: srcTracker.stage,
    status: srcTracker.status,
    extra: parseTrackerExtra(srcTracker.extra),
    bumpedAt: srcTracker.bumped_at,
    seenAt: Number.isInteger(seenAt) ? seenAt : undefined,
    autoSeenAt: Number.isInteger(autoSeenAt) ? autoSeenAt : undefined,
    recipient: (srcTracker.recipient && parseRecipient(srcTracker.recipient)) || undefined,
  };
}

const validTrackerFlags = values(ConversationTrackerFlagsEnum);
function parseTrackerExtra(src: any) {
  return src ? src.filter((flag: any) => validTrackerFlags.includes(flag)) : [];
}
